import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Iconify from '../reusable-components/iconify';
import { useScopeCheckFn } from '../reusable-components/scopes';
import { useSelector } from '../redux/store';
import { NotificationMetricType, selectAdjustedMetrics } from '../redux/slices/notifications';

const NOTIFICATION_PARAM = 'notification';

const notificationMetricScopes = {
  [NotificationMetricType.OrdersNeedApproval]: ['View-Order'],
  [NotificationMetricType.OrdersSubmitted]: ['Create-Order'],
  [NotificationMetricType.InvoicesNeedApproval]: ['Invoice-View', 'ManualInvoice-View'],
  [NotificationMetricType.InvoicesSubmitted]: ['ManualInvoice-Submit'],
  [NotificationMetricType.RentalsNeedApproval]: ['Rental-Order-Approve'],
  [NotificationMetricType.RentalsSubmitted]: ['Rental-Order-Submit'],
};

export const withNotificationFilter = (path, count) => {
  if (!count) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${NOTIFICATION_PARAM}=true`;
};

export function useNotificationMetricAccess() {
  const scopeCheck = useScopeCheckFn();
  return (type) => {
    const scopes = notificationMetricScopes[type];
    return !scopes || !!scopeCheck(scopes, true);
  };
}

const selectHasFailed = (state) => !!state.notifications.error && !state.notifications.hasLoaded;

export function useNotificationIdsFilter(types) {
  const [searchParams] = useSearchParams();
  const metrics = useSelector(selectAdjustedMetrics);
  const hasLoaded = useSelector((state) => state.notifications.hasLoaded);
  const hasFailed = useSelector(selectHasFailed);
  const isFiltering = searchParams.get(NOTIFICATION_PARAM) === 'true' && !hasFailed;
  const hasAccess = useNotificationMetricAccess();
  const allowedTypes = types.filter(hasAccess).join(',');
  const notificationIds = useMemo(
    () => new Set(allowedTypes ? allowedTypes.split(',').flatMap((type) => metrics[type].ids) : []),
    [allowedTypes, metrics]
  );
  const matchesNotificationIds = useCallback(
    (...ids) => !isFiltering || ids.some((id) => notificationIds.has(String(id))),
    [isFiltering, notificationIds]
  );
  return { matchesNotificationIds, isNotificationLoading: isFiltering && !hasLoaded };
}

export function NotificationIdsChip({ label }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasFailed = useSelector(selectHasFailed);
  if (searchParams.get(NOTIFICATION_PARAM) !== 'true' || hasFailed) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        px: 2,
        py: 0.75,
        mb: 1,
        bgcolor: '#EEEFF0',
        borderRadius: '8px',
        outline: '1px solid rgba(145, 158, 171, 0.15)',
        outlineOffset: '-1px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', py: '7px', pr: 1.5 }}>
          <Iconify icon="mdi:filter" width={24} sx={{ color: '#454F5B' }} />
        </Box>
        <Typography sx={{ flex: 1, py: 1, color: '#212B36', fontSize: 16, fontWeight: 400, wordWrap: 'break-word' }}>
          Showing {label} needing your attention.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          onClick={() => setSearchParams((prev) => { prev.delete(NOTIFICATION_PARAM); return prev; })}
          endIcon={<ChevronRightIcon />}
          sx={{
            px: 2,
            py: 0.5,
            bgcolor: 'background.paper',
            borderRadius: '25px',
            outline: '1px solid #DFE3E8',
            outlineOffset: '-1px',
            color: '#454F5B',
            fontSize: 14,
            fontWeight: 500,
            textTransform: 'capitalize',
            lineHeight: '24px',
            letterSpacing: '0.06px',
            '& .MuiButton-endIcon': { ml: 0.5 },
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          View all {label}
        </Button>
      </Box>
    </Box>
  );
}
